import {Draft} from 'immer';
import {initial, last} from 'lodash';
import {Dispatch} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DataFieldsTableModel} from '../../../models/api/dataFieldsTableModel';
import {SettingsCustomFieldsPagesEnum} from '../../../models/url/settingsUrlModel';
import {postApiAsync} from '../../../services/http/http';
import {CustomFields} from '../commons/customFieldsModel';
import {ExpressionAction, ExpressionActionTypesEnum} from './expressionActions';
import {ClientAtom} from './expressionAtomModel';
import {parseExpression} from './expressionInferHelpers';
import {Expression, ExpressionId, PreExpression} from './expressionModel';
import {createExpressionsContext} from './expressionsContext';
import {InferredType} from './infer/expressionResultModel';
import {ApiExpressionsDryRunResult} from './iotsCodecs';

/*
 * Types.
 */

export interface ExpressionState {
  expressions: ReadonlyArray<Expression>;
  initialInfer: InferredType;
  customFields: CustomFields;
  expressionPanelState: {
    isOpen: boolean;
    selectedExpression: Expression | null;
  };
  maxId: number;
  hasDryRunTestData: boolean; // should we disable the "Dry run" button?
  dryRunErrorMessage: string | undefined;
}

export interface ExpressionStateActions {
  onAddExpression: (atom: ReadonlyArray<ClientAtom>) => void;
  onUpdateExpressionName: (expressionId: ExpressionId, name: string) => void;
  onDeleteExpression: HandlerOf<ExpressionId>;

  onAddAtom: (expressionId: ExpressionId, atom: ClientAtom, functionAtomClientId?: string) => void;
  onUpdateAtom: (
    expressionId: ExpressionId,
    atomClientId: string,
    update: (atom: ClientAtom) => ClientAtom,
  ) => void;
  onDeleteAtom: (expressionId: ExpressionId, atomClientId: string) => void;

  onUpdateDryRun: (dryRunEndpoint: string, baseParams: Record<string, unknown>) => Promise<void>;
  onClearDryRun: Handler;
  onDryRunError: HandlerOf<string>;

  onSetPanelOpen: HandlerOf<boolean>;
  onSaveDraftExpression: Handler;
  onSelectExpression: HandlerOf<ExpressionId>;
}

export type ExpressionValidator<S extends ExpressionState> = HandlerOf<Draft<S>>;

export interface ExpressionDependencies {
  dataTable?: DataFieldsTableModel;
}

interface UseExpressionStateValidators<S extends ExpressionState> {
  onAddExpressionValidator?: ExpressionValidator<S>;
  makeOnDeleteExpressionValidator?: (expressionId: ExpressionId) => ExpressionValidator<S>;
  makeOnAddAtomValidator?: (expressionId: ExpressionId) => ExpressionValidator<S>;
  makeOnUpdateAtomValidator?: (expressionId: ExpressionId) => ExpressionValidator<S>;
  makeOnDeleteAtomValidator?: (expressionId: ExpressionId) => ExpressionValidator<S>;
}

/*
 * State.
 */

export const ExpressionState = {
  makeDefault(initialInfer: InferredType): ExpressionState {
    return {
      expressions: [],
      initialInfer,
      customFields: {
        [SettingsCustomFieldsPagesEnum.CARDS]: [],
        [SettingsCustomFieldsPagesEnum.TEAMMATES]: [],
        [SettingsCustomFieldsPagesEnum.INBOXES]: [],
        [SettingsCustomFieldsPagesEnum.ACCOUNTS]: [],
      },
      expressionPanelState: {
        isOpen: false,
        selectedExpression: null,
      },
      maxId: 0,
      hasDryRunTestData: false,
      dryRunErrorMessage: undefined,
    };
  },

  createInitialState(
    preExpressions: ReadonlyArray<PreExpression>,
    customFields: CustomFields,
    initialInfer: InferredType,
    dependencies: ExpressionDependencies,
  ): ExpressionState {
    const expressions: Expression[] = [];

    preExpressions.forEach((preExpression) => {
      const context = createExpressionsContext({expressions, customFields, initialInfer});
      expressions.push(parseExpression(preExpression, context, dependencies));
    });

    return {
      ...this.makeDefault(initialInfer),
      expressions,
      customFields,
      maxId: computeMaxId(expressions),
    };
  },
};

/*
 * Hook.
 */

export function useExpressionState<S extends ExpressionState>(
  {
    onAddExpressionValidator,
    makeOnDeleteExpressionValidator,
    makeOnAddAtomValidator,
    makeOnUpdateAtomValidator,
    makeOnDeleteAtomValidator,
  }: UseExpressionStateValidators<S>,
  state: ExpressionState,
  dispatch: Dispatch<ExpressionAction<S>>,
): ExpressionStateActions {
  const onAddExpression = (atoms: ReadonlyArray<ClientAtom>) => {
    dispatch({type: ExpressionActionTypesEnum.ADD_EXPRESSION, atoms});
  };

  const onUpdateExpressionName = (expressionId: ExpressionId, name: string) => {
    dispatch({
      type: ExpressionActionTypesEnum.UPDATE_EXPRESSION_NAME,
      expressionId,
      name,
    });
  };

  const onDeleteExpression = (expressionId: ExpressionId) => {
    dispatch({
      type: ExpressionActionTypesEnum.DELETE_EXPRESSION,
      expressionId,
      validator: makeOnDeleteExpressionValidator?.(expressionId),
    });
  };

  const onAddAtom = (
    expressionId: ExpressionId,
    atom: ClientAtom,
    functionAtomClientId: string | undefined,
  ) => {
    dispatch({
      type: ExpressionActionTypesEnum.ADD_ATOM,
      expressionId,
      functionAtomClientId,
      atom,
      validator: makeOnAddAtomValidator?.(expressionId),
    });
  };

  const onUpdateAtom = (
    expressionId: ExpressionId,
    atomClientId: string,
    update: (atom: ClientAtom) => ClientAtom,
  ) => {
    dispatch({
      type: ExpressionActionTypesEnum.UPDATE_ATOM,
      expressionId,
      atomClientId,
      update,
      validator: makeOnUpdateAtomValidator?.(expressionId),
    });
  };

  const onDeleteAtom = (expressionId: ExpressionId, atomClientId: string) => {
    dispatch({
      type: ExpressionActionTypesEnum.DELETE_ATOM,
      expressionId,
      atomClientId,
      validator: makeOnDeleteAtomValidator?.(expressionId),
    });
  };

  const onClearDryRun = () => {
    dispatch({type: ExpressionActionTypesEnum.CLEAR_DRY_RUN});
  };

  const onUpdateDryRun = async (dryRunEndpoint: string, baseParams: Record<string, unknown>) => {
    const {
      expressions,
      expressionPanelState: {selectedExpression},
    } = state;

    // Include selected expression because it contains unsaved changes that we want to inspect.
    const hasSelectedExpression = Boolean(selectedExpression);
    const allExpressions = hasSelectedExpression ? [...expressions, selectedExpression] : expressions;

    onClearDryRun();

    const result = await postApiAsync(ApiExpressionsDryRunResult, dryRunEndpoint, {
      ...baseParams,
      expressions: allExpressions,
    });

    const dryRunExpressions = hasSelectedExpression ? initial(result.expressions) : result.expressions;
    const dryRunSelectedExpression = (hasSelectedExpression && last(result.expressions)) || null;

    dispatch({
      type: ExpressionActionTypesEnum.UPDATE_DRY_RUN,
      expressions: dryRunExpressions,
      selectedExpression: dryRunSelectedExpression,
    });
  };

  const onDryRunError = (errorMessage: string) => {
    dispatch({type: ExpressionActionTypesEnum.DRY_RUN_ERROR, errorMessage});
  };

  const onSetPanelOpen = (isOpen: boolean) => {
    dispatch({type: ExpressionActionTypesEnum.SET_PANEL_OPEN, isOpen});
  };

  const onSaveDraftExpression = () => {
    dispatch({type: ExpressionActionTypesEnum.SAVE_DRAFT_EXPRESSION, validator: onAddExpressionValidator});
  };

  const onSelectExpression = (id: ExpressionId) => {
    dispatch({type: ExpressionActionTypesEnum.SELECT_EXPRESSION, id});
  };

  return {
    onAddExpression,
    onUpdateExpressionName,
    onDeleteExpression,

    onAddAtom,
    onUpdateAtom,
    onDeleteAtom,

    onUpdateDryRun,
    onClearDryRun,
    onDryRunError,

    onSetPanelOpen,
    onSaveDraftExpression,
    onSelectExpression,
  };
}

/*
 * Helpers.
 */

function computeMaxId(expressions: ReadonlyArray<Expression>) {
  return expressions.reduce((maxId, expression) => {
    const numericId = parseExpressionId(expression.id);
    return isFinite(numericId) ? Math.max(numericId, maxId) : maxId;
  }, 0);
}

function parseExpressionId(id: ExpressionId) {
  return Number(id.replace('local:', '')) ?? undefined;
}
